import { useEffect, useState } from 'react';
import { api, type Experiment } from '../lib/api.ts';
import { Button, DemoBadge, EmptyState, ErrorNote, SectionTitle, Spinner } from '../components/ui.tsx';
import { longDate, titleCase } from '../lib/format.ts';

const RUNNING = ['baseline', 'active', 'paused'];

export function Experiments({ navigate }: { navigate: (path: string) => void }) {
  const [experiments, setExperiments] = useState<Experiment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.experiments.list()
      .then((r) => setExperiments(r.experiments))
      .catch((e) => setError((e as Error).message));
  }, []);

  if (error) return <ErrorNote message={error} />;
  if (!experiments) return <Spinner />;

  const running = experiments.filter((e) => RUNNING.includes(e.status));
  const drafts = experiments.filter((e) => e.status === 'draft');
  const done = experiments.filter((e) => e.status === 'completed' || e.status === 'abandoned');

  return (
    <div className="space-y-6">
      <Button variant="primary" full onClick={() => navigate('/experiments/new')}>New experiment</Button>

      {experiments.length === 0 && (
        <EmptyState
          title="No experiments yet"
          detail="An experiment is a plan, a baseline, and something you measure every day. Start with a template."
        />
      )}

      {[
        { title: 'Running', items: running },
        { title: 'Drafts', items: drafts },
        { title: 'Finished', items: done },
      ].filter((g) => g.items.length > 0).map((group) => (
        <section key={group.title}>
          <SectionTitle>{group.title}</SectionTitle>
          <ul className="space-y-2">
            {group.items.map((experiment) => (
              <li key={experiment.id}>
                <button
                  onClick={() => navigate(`/experiments/${experiment.id}`)}
                  className="card w-full text-left p-4 active:opacity-80"
                >
                  <div className="flex items-start gap-2">
                    <p className="font-medium flex-1">{experiment.name.replace(/^\[demo\]\s*/, '')}</p>
                    {experiment.is_demo && <DemoBadge />}
                  </div>
                  {experiment.goal && <p className="text-sm text-ink-2 mt-1">{experiment.goal}</p>}
                  <p className="text-xs text-muted mt-2">
                    {titleCase(experiment.status)}
                    {experiment.intervention_start && ` · started ${longDate(experiment.intervention_start)}`}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
